import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { apiService } from '../services/apiService';
import Stamp from './layout/Stamp';

// Interfaces
interface SessionSummary {
  overview: string;
  keyDiscussionPoints: string[];
  actionItems: string[];
  insights: string[];
  progressNotes: string;
  recommendations: string[];
  studentStrengths?: string[];
  areasForGrowth?: string[];
}

interface SessionReviewData {
  recordingUrl: string | null;
  recordingStatus: string | null;
  transcript: string | null;
  transcriptionStatus: string | null;
  summary: SessionSummary | null;
  summaryGeneratedAt: string | null;
}

// Component
const SessionReview: React.FC = () => {
  const { sessionType, sessionId } = useParams<{ sessionType: string; sessionId: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<SessionReviewData | null>(null);

  // Rating card
  const [rating, setRating] = useState(0);
  const [reviewText, setReviewText] = useState('');
  const [reviewSubmitted, setReviewSubmitted] = useState(false);
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [reviewError, setReviewError] = useState<string | null>(null);

  useEffect(() => {
    const fetchReview = async () => {
      if (!sessionType || !sessionId) {
        setError('Invalid session parameters');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const data = await apiService.get(`/video-rooms/recording/${sessionType}/${sessionId}`);
        // Check if there was an error in the response
        if (data.error) {
          setError(data.error);
        } else {
          setData(data);
        }
        setLoading(false);
      } catch (err: any) {
        console.error('Failed to fetch session review:', err);
        setError(err.message || 'Failed to load session review');
        setLoading(false);
      }
    };

    fetchReview();
  }, [sessionType, sessionId]);

  // Parse transcript into lines
  const parseTranscript = (transcript: string): Array<{ timestamp: string; speaker: string; text: string }> => {
    const lines = transcript.split('\n\n');
    return lines.map(line => {
      const match = line.match(/\[(\d{2}:\d{2})\]\s*(Speaker\s*\w+):\s*(.*)/s);
      if (match) {
        return { timestamp: match[1], speaker: match[2], text: match[3] };
      }
      return { timestamp: '', speaker: '', text: line };
    }).filter(l => l.text.trim());
  };

  const handleSubmitReview = async () => {
    if (rating === 0 || reviewSubmitting) return;
    setReviewError(null);

    // Only mentor sessions have a review endpoint; counselor stays UI-only
    if (sessionType !== 'mentor' || !sessionId) {
      setReviewSubmitted(true);
      return;
    }

    setReviewSubmitting(true);
    const result = await apiService.submitMentorReview(sessionId, {
      rating,
      comment: reviewText.trim() || undefined,
    });
    setReviewSubmitting(false);

    if (result.success || result.error === 'already_reviewed') {
      setReviewSubmitted(true);
    } else {
      setReviewError(result.error || 'Failed to submit review. Please try again.');
    }
  };

  const ratingCard = (
    <div className="card" style={{ marginBottom: 16 }}>
      <h3 style={{ marginBottom: 4 }}>Was it worth it?</h3>
      <p className="meta" style={{ marginBottom: 12 }}>
        Your rating is published on the mentor's profile with the booking verified.
      </p>
      {reviewSubmitted ? (
        <div className="note">
          Thanks — your review helps other families judge whether this mentor is worth it.
        </div>
      ) : (
        <>
          <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                onClick={() => setRating(star)}
                aria-label={`${star} star${star > 1 ? 's' : ''}`}
                style={{
                  border: 'none',
                  background: 'none',
                  fontSize: 22,
                  cursor: 'pointer',
                  color: star <= rating ? 'var(--cardinal)' : 'var(--hairline)',
                  padding: 0,
                }}
              >
                ★
              </button>
            ))}
          </div>
          <textarea
            rows={3}
            value={reviewText}
            onChange={(e) => setReviewText(e.target.value)}
            placeholder="One sentence other families should know."
            style={{
              width: '100%',
              border: '1px solid var(--hairline)',
              borderRadius: 8,
              padding: 10,
              fontSize: 13.5,
              lineHeight: 1.6,
              fontFamily: 'inherit',
              resize: 'vertical',
              marginBottom: 12,
            }}
          />
          {reviewError && (
            <p className="meta" style={{ color: 'var(--cardinal)', marginBottom: 10 }}>
              {reviewError}
            </p>
          )}
          <button
            className="btn btn-primary btn-sm"
            disabled={rating === 0 || reviewSubmitting}
            style={rating === 0 || reviewSubmitting ? { opacity: 0.5 } : undefined}
            onClick={handleSubmitReview}
          >
            {reviewSubmitting ? 'Submitting…' : 'Submit review'}
          </button>
        </>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="section" style={{ paddingTop: 40 }}>
        <div className="wrap" style={{ maxWidth: 980 }}>
          <div className="card" style={{ padding: 32 }}>
            <span className="eyebrow" style={{ display: 'block', marginBottom: 10 }}>
              After the session
            </span>
            <h1 style={{ fontSize: 24, marginBottom: 8 }}>Loading your session review…</h1>
            <p className="meta">Fetching the recording, notes, and summary.</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="section" style={{ paddingTop: 40 }}>
        <div className="wrap" style={{ maxWidth: 560 }}>
          <div className="card" style={{ padding: 32 }}>
            <span className="eyebrow" style={{ display: 'block', marginBottom: 10 }}>
              After the session
            </span>
            <h1 style={{ fontSize: 24, marginBottom: 8 }}>Unable to load this review</h1>
            <p className="meta" style={{ marginBottom: 20 }}>{error}</p>
            <button className="btn btn-secondary" onClick={() => navigate(-1)}>
              Go back
            </button>
          </div>
          {/* Recording failed to load, but mentor sessions can still be rated */}
          {sessionType === 'mentor' && <div style={{ marginTop: 16 }}>{ratingCard}</div>}
        </div>
      </div>
    );
  }

  const isProcessing =
    data?.recordingStatus === 'processing' || data?.transcriptionStatus === 'processing';

  return (
    <div className="section" style={{ paddingTop: 40 }}>
      <div className="wrap" style={{ maxWidth: 980 }}>
        <Stamp variant="verified" style={{ marginBottom: 12 }}>
          Session complete — notes delivered to the family
        </Stamp>
        <h1 style={{ fontSize: 28, marginTop: 8 }}>After the session</h1>
        <p style={{ color: 'var(--ink-secondary)', marginTop: 8, maxWidth: '60ch' }}>
          The recording, the shared notes, and the written next steps land here. Then one question
          — was it worth it?
        </p>

        {isProcessing && (
          <div className="note" style={{ marginTop: 20 }}>
            Processing in progress — the recording is being transcribed and summarized. Check back
            shortly.
          </div>
        )}

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1.5fr 1fr',
            gap: 24,
            alignItems: 'start',
            marginTop: 28,
          }}
        >
          {/* Left column — recording, next steps, transcript */}
          <div style={{ minWidth: 0 }}>
            <div className="card" style={{ marginBottom: 16 }}>
              <h3 style={{ marginBottom: 12 }}>Recording</h3>
              {data?.recordingUrl ? (
                <>
                  <video
                    controls
                    src={data.recordingUrl}
                    style={{
                      width: '100%',
                      borderRadius: 12,
                      background: '#1f1e1b',
                      marginBottom: 10,
                    }}
                  >
                    Your browser does not support the video tag.
                  </video>
                  <p className="meta">
                    Available to the family for 12 months.
                    {data?.transcript ? ' Transcript below.' : ''}
                  </p>
                </>
              ) : (
                <div className="ph" style={{ aspectRatio: '16 / 9' }}>
                  <span>
                    {isProcessing ? 'recording processing…' : 'recording not available'}
                  </span>
                </div>
              )}
            </div>

            {data?.summary && (data.summary.actionItems?.length ?? 0) > 0 && (
              <div className="card" style={{ marginBottom: 16 }}>
                <h3 style={{ marginBottom: 12 }}>Written next steps</h3>
                <table className="data">
                  <tbody>
                    {data.summary.actionItems.map((item, i) => (
                      <tr key={i}>
                        <td className="num-cell" style={{ width: 28 }}>{i + 1}</td>
                        <td>{item}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {data?.transcript && (
              <div className="card">
                <h3 style={{ marginBottom: 12 }}>Transcript</h3>
                <div style={{ maxHeight: 420, overflowY: 'auto' }}>
                  {parseTranscript(data.transcript).map((line, index) => (
                    <div
                      key={index}
                      style={{
                        padding: '10px 12px',
                        borderLeft: '2px solid var(--hairline)',
                        marginBottom: 8,
                      }}
                    >
                      <div className="meta" style={{ marginBottom: 2 }}>
                        {line.timestamp && <span style={{ marginRight: 8 }}>{line.timestamp}</span>}
                        {line.speaker && (
                          <span style={{ fontWeight: 500, color: 'var(--cardinal)' }}>
                            {line.speaker}
                          </span>
                        )}
                      </div>
                      <p style={{ fontSize: 14, lineHeight: 1.6 }}>{line.text}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right column — summary, rating, rebook */}
          <aside style={{ minWidth: 0 }}>
            {ratingCard}

            {data?.summary && (
              <div className="card" style={{ marginBottom: 16 }}>
                <h3 style={{ marginBottom: 12 }}>Session summary</h3>
                {data.summary.overview && (
                  <div style={{ marginBottom: 16 }}>
                    <div className="eyebrow" style={{ marginBottom: 6 }}>Overview</div>
                    <p style={{ fontSize: 14, lineHeight: 1.6 }}>{data.summary.overview}</p>
                  </div>
                )}
                {(data.summary.keyDiscussionPoints?.length ?? 0) > 0 && (
                  <div style={{ marginBottom: 16 }}>
                    <div className="eyebrow" style={{ marginBottom: 6 }}>What was covered</div>
                    <ul style={{ paddingLeft: 18, margin: 0 }}>
                      {data.summary.keyDiscussionPoints.map((point, i) => (
                        <li key={i} style={{ fontSize: 14, lineHeight: 1.6, marginBottom: 6 }}>
                          {point}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {(data.summary.recommendations?.length ?? 0) > 0 && (
                  <div style={{ marginBottom: 16 }}>
                    <div className="eyebrow" style={{ marginBottom: 6 }}>Recommendations</div>
                    <ul style={{ paddingLeft: 18, margin: 0 }}>
                      {data.summary.recommendations.map((rec, i) => (
                        <li key={i} style={{ fontSize: 14, lineHeight: 1.6, marginBottom: 6 }}>
                          {rec}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {data.summary.progressNotes && (
                  <div style={{ marginBottom: 16 }}>
                    <div className="eyebrow" style={{ marginBottom: 6 }}>Progress notes</div>
                    <p style={{ fontSize: 14, lineHeight: 1.6 }}>{data.summary.progressNotes}</p>
                  </div>
                )}
                {((data.summary.studentStrengths?.length ?? 0) > 0 ||
                  (data.summary.areasForGrowth?.length ?? 0) > 0) && (
                  <div>
                    <div className="eyebrow" style={{ marginBottom: 6 }}>Student development</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {data.summary.studentStrengths?.map((s, i) => (
                        <span key={`s-${i}`} className="tag">{s}</span>
                      ))}
                      {data.summary.areasForGrowth?.map((a, i) => (
                        <span key={`g-${i}`} className="tag">{a}</span>
                      ))}
                    </div>
                  </div>
                )}
                {data.summaryGeneratedAt && (
                  <p className="meta" style={{ marginTop: 14 }}>
                    Generated {new Date(data.summaryGeneratedAt).toLocaleDateString()}
                  </p>
                )}
              </div>
            )}

            {!data?.summary && !isProcessing && (
              <div className="card" style={{ marginBottom: 16 }}>
                <h3 style={{ marginBottom: 8 }}>Session summary</h3>
                <p className="meta">
                  The written summary for this session hasn't been generated yet.
                </p>
              </div>
            )}

            <div className="card warm">
              <h3 style={{ fontSize: 16, marginBottom: 8 }}>Keep going</h3>
              <p className="meta" style={{ marginBottom: 12 }}>
                Momentum matters — book the next session while the notes are fresh.
              </p>
              <Link className="btn btn-secondary btn-sm" to="/mentors">
                Book another session
              </Link>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default SessionReview;
